// Sezione Articoli: provenienze abituali (codici macello, nato/allevato) per articolo
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { wmsApi } from "./wmsApi";
import { WCard, WBadge, WInput, WField, WButton } from "./WmsUi";

const EMPTY = { birth: "", rearing: "", slaughter: "", slaughter_code: "", cutting: "", cutting_code: "" };

export const PresetChip = ({ p, onClick, onDelete }) => (
  <div className="inline-flex items-center border-2 border-zinc-300 bg-white">
    <button onClick={onClick} className="px-3 h-11 text-left hover:bg-orange-50" data-testid="preset-chip">
      <span className="text-xs font-bold">
        {[p.birth && `N:${p.birth}`, p.rearing && `A:${p.rearing}`, p.slaughter_code || p.slaughter, p.cutting_code].filter(Boolean).join(" · ") || "—"}
      </span>
      {p.times > 0 && <span className="text-[10px] text-zinc-400 ml-1">×{p.times}</span>}
    </button>
    {onDelete && <button onClick={onDelete} className="px-2 h-11 text-red-500 hover:bg-red-50 border-l border-zinc-200" data-testid="preset-delete"><Trash2 className="w-4 h-4" /></button>}
  </div>
);

export default function WmsArticles() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState({});
  const [form, setForm] = useState(EMPTY);
  const [formPid, setFormPid] = useState(null);

  const load = useCallback(() => {
    wmsApi.get("/wms/articles", { params: { q: q || undefined } }).then((r) => setRows(r.data)).catch(() => {});
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const addPreset = async (pid) => {
    try {
      await wmsApi.post(`/wms/products/${pid}/origin-presets`, form);
      toast.success("Provenienza salvata");
      setForm(EMPTY); setFormPid(null); load();
    } catch { toast.error("Errore salvataggio"); }
  };

  const delPreset = async (pid, idx) => {
    await wmsApi.delete(`/wms/products/${pid}/origin-presets/${idx}`).catch(() => {});
    load();
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-['Chivo'] font-black text-3xl tracking-tighter uppercase">Articoli</h1>
        <p className="text-zinc-500 mt-1">Provenienze abituali per articolo (codici macello, nato/allevato) — imparate dai carichi, modificabili qui</p>
      </div>
      <div className="relative max-w-md">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
        <WInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca articolo o categoria..." className="pl-12" data-testid="articles-search" />
      </div>
      <div className="space-y-2">
        {rows.map((a) => (
          <WCard key={a.id} className="border-2 border-zinc-300">
            <button className="w-full p-4 flex items-center gap-3 text-left" onClick={() => setOpen({ ...open, [a.id]: !open[a.id] })} data-testid={`article-row-${a.code}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold truncate">{a.name}</p>
                  <WBadge tone="dark">{a.category_name || "—"}</WBadge>
                </div>
                <p className="text-xs text-zinc-400 font-mono">{a.code}</p>
              </div>
              <WBadge tone={a.presets.length ? "orange" : "zinc"}>{a.presets.length} provenienze</WBadge>
              {open[a.id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {open[a.id] && (
              <div className="border-t border-zinc-200 p-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {a.presets.map((p, idx) => <PresetChip key={idx} p={p} onDelete={() => delPreset(a.id, idx)} />)}
                  {a.presets.length === 0 && <p className="text-sm text-zinc-400">Nessuna provenienza memorizzata: si imparano dai carichi.</p>}
                </div>
                {formPid === a.id ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border-2 border-zinc-900 bg-white p-3">
                    <WField label="Nato in"><WInput value={form.birth} onChange={(e) => setForm({ ...form, birth: e.target.value })} className="!h-11" /></WField>
                    <WField label="Allevato in"><WInput value={form.rearing} onChange={(e) => setForm({ ...form, rearing: e.target.value })} className="!h-11" /></WField>
                    <WField label="Macellato in"><WInput value={form.slaughter} onChange={(e) => setForm({ ...form, slaughter: e.target.value })} className="!h-11" /></WField>
                    <WField label="Bollo macello"><WInput value={form.slaughter_code} onChange={(e) => setForm({ ...form, slaughter_code: e.target.value })} className="!h-11" /></WField>
                    <WField label="Sezionato in"><WInput value={form.cutting} onChange={(e) => setForm({ ...form, cutting: e.target.value })} className="!h-11" /></WField>
                    <WField label="Bollo sezionamento"><WInput value={form.cutting_code} onChange={(e) => setForm({ ...form, cutting_code: e.target.value })} className="!h-11" /></WField>
                    <WButton variant="primary" className="col-span-2 md:col-span-3 !h-12" onClick={() => addPreset(a.id)} data-testid={`preset-save-${a.code}`}>Salva provenienza</WButton>
                  </div>
                ) : (
                  <WButton className="!h-12" onClick={() => { setFormPid(a.id); setForm(EMPTY); }} data-testid={`preset-add-${a.code}`}><Plus className="w-4 h-4" /> Aggiungi provenienza</WButton>
                )}
              </div>
            )}
          </WCard>
        ))}
      </div>
    </div>
  );
}
